import {
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerProps,
  HStack,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { Fragment, useEffect, useState } from 'react';
import { useTranslations } from 'use-intl';

import {
  BalanceInfosFragment,
  Identity,
  IdentityType,
  OptionAssetGrantFragment,
  OptionAssetInfosFragment,
  useGetGrantsFromAssetLazyQuery,
  useGetOptionAssetLazyQuery,
} from '@blockpulse3/graphql/hooks';
import { formatDate, formatNumberCurrency, formatNumberInt } from '@blockpulse3/helpers';
import {
  AssetBadge,
  CompanyIdentityAvatar,
  IdentityAvatar,
  IdentityCard,
  IdentityCardDescription,
  IdentityCardTitle,
} from '@blockpulse3/ui/commons';

type Props = {
  balance: BalanceInfosFragment | null;
  identityId: Identity['id'] | null;
} & Omit<DrawerProps, 'children'>;

export function OptionAssetSidePanel({ balance, identityId, ...props }: Props): JSX.Element {
  const i18nOptionAssetSidePanel = useTranslations('OptionAssetSidePanel');

  const [getOptionAssetQuery] = useGetOptionAssetLazyQuery();
  const [getGrantsFromAsset] = useGetGrantsFromAssetLazyQuery();

  const [grants, setGrants] = useState<OptionAssetGrantFragment[] | null>(null);
  const [optionAsset, setOptionAsset] = useState<OptionAssetInfosFragment | null>(null);

  const { isOpen } = props;
  useEffect(() => {
    if (!identityId || !balance || !isOpen) return;

    getGrantsFromAsset({
      variables: {
        grantsFromAssetInput: {
          assetId: balance.asset.id,
          companyId: balance.asset.company.id,
          identityId,
        },
      },
      onCompleted: (data) => {
        setGrants(data.getGrantsFromAsset);
      },
    });

    getOptionAssetQuery({
      variables: {
        getAssetInput: {
          assetId: balance.asset.id,
          companyId: balance.asset.company.id,
        },
      },
      onCompleted: (data) => {
        setOptionAsset(data.getOptionAsset);
      },
      onError: (err) => {
        console.log('Error', err);
      },
    });
  }, [balance, identityId]);

  if (!optionAsset || !grants) {
    return (
      <Drawer {...props}>
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerBody p="0">
            <Stack alignItems="center" h="full" justifyContent="center" py="4" spacing="4">
              <Spinner />
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  const { signer, contact } = optionAsset.asset.company;
  const repIndividualIdentity = contact?.individualIdentity || signer.individualIdentity;

  return (
    <Drawer {...props}>
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerBody p="0">
          <Stack py="4" spacing="4">
            <Stack alignItems="center" px="4" spacing="2">
              <Stack alignItems="center" spacing="1">
                <CompanyIdentityAvatar
                  boxSize="20"
                  src={optionAsset.asset.company.identity?.profilePicture}
                />
                <Text color="gray.800" fontWeight="800">
                  {optionAsset.asset.company.name}
                </Text>
              </Stack>
            </Stack>
            <Divider />
            <Stack fontSize="sm" px="4" spacing="6">
              <HStack justifyContent="space-between">
                <Text color="gray.500" fontWeight="500">
                  {i18nOptionAssetSidePanel('AssetTitle')}
                </Text>
                <AssetBadge type={optionAsset.assetType} value={null} />
              </HStack>
              <HStack justifyContent="space-between">
                <Text color="gray.500" fontWeight="500">
                  {i18nOptionAssetSidePanel('AssetPurchasePrice')}
                </Text>
                <Text fontWeight="500">{formatNumberCurrency(optionAsset.subscriptionPrice)}</Text>
              </HStack>
              <HStack justifyContent="space-between">
                <Text color="gray.500" fontWeight="500">
                  {i18nOptionAssetSidePanel('AssetExercisedPrice')}
                </Text>
                <Text fontWeight="500">{formatNumberCurrency(optionAsset.exercisePrice)}</Text>
              </HStack>
            </Stack>
            <Divider />

            {grants.map((grant, index) => (
              <Fragment key={index}>
                <Stack fontSize="sm" px="4" spacing="4">
                  <HStack>
                    <Text color="gray.600" fontSize="md" fontWeight="600">
                      {i18nOptionAssetSidePanel('TitreGrant', {
                        date: formatDate(dayjs.unix(grant.date).toDate(), 'D MMMM YYYY'),
                      })}
                    </Text>
                  </HStack>
                  <HStack justifyContent="space-between">
                    <Text color="gray.500" fontWeight="500">
                      {i18nOptionAssetSidePanel('AssetQuantity')}
                    </Text>
                    <Text fontWeight="500">{formatNumberInt(grant.amount || 0)}</Text>
                  </HStack>
                  <HStack justifyContent="space-between">
                    <Text color="gray.500" fontWeight="500">
                      {i18nOptionAssetSidePanel('VestedQuantity')}
                    </Text>
                    <Text fontWeight="500">{formatNumberInt(grant.vestedAmount || 0)}</Text>
                  </HStack>
                  <HStack justifyContent="space-between">
                    <Text color="gray.500" fontWeight="500">
                      {i18nOptionAssetSidePanel('ExercisedQuantity')}
                    </Text>
                    <Text fontWeight="500">{formatNumberInt(grant.exercisedAmount)}</Text>
                  </HStack>
                </Stack>
                <Divider />
              </Fragment>
            ))}
            <Stack px="4" spacing="4">
              <Text color="gray.600" fontWeight="600">
                {i18nOptionAssetSidePanel(contact?.individualIdentity ? 'Contact' : 'President')}
              </Text>
              <IdentityCard bg="gray.50" p="3">
                <IdentityAvatar type={IdentityType.INDIVIDUAL} />
                <Stack spacing="0">
                  <IdentityCardTitle isChecked>
                    <Text>{repIndividualIdentity?.name}</Text>
                  </IdentityCardTitle>
                  <IdentityCardDescription>
                    <Text>{repIndividualIdentity?.email}</Text>
                  </IdentityCardDescription>
                </Stack>
              </IdentityCard>
            </Stack>
          </Stack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

export type OptionAssetSidePanelProps = Props;
